import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Button, Input, Select, SearchableSelect } from '../atoms';
import type { FormValues } from '../organisms';
import { Wizard, useToast } from '../organisms';
import { ParcelDocumentIntake, type ParcelProposal } from '../organisms/ParcelDocumentIntake';
import { ParcelDuplicateWarning } from './ParcelsList';
import { usePermissions } from '../lib/usePermissions';
import { bookAssignmentFor } from '../lib/parcelBookAssignment';

/**
 * ParcelSetupWizard — /v2/parcels/new (§3).
 *
 * Replaces the long-scroll New Parcel popup with the same horizontal stepped
 * flow as new client / property setup. Supports creating MULTIPLE parcels in one
 * pass (§3.2): the property + jurisdictions + assessor are chosen once and carried
 * forward; only the per-parcel fields (account number, RE/PP) loop. On creation it
 * prompts whether to extend the property's existing contact(s) to the new parcels
 * (§3.3), instead of silently auto-covering them.
 *
 * ?property_id= pre-selects (and locks) the property — used by the "New parcel"
 * button on a property profile.
 */

interface PropertyRow { id: string; name: string | null; state: string | null; client_id: string | null }
interface AssessorRow { id: string; name: string | null }
interface JurisdictionRow { id: string; name: string | null }
interface ParcelRow { id: string; assessor_id: string | null }
interface PropContact { id: string; first_name: string | null; last_name: string | null; company_name: string | null; role: string | null }

interface ParcelDraft { account_number: string; re_pp: string; name: string; owner_name: string }
const emptyParcel = (): ParcelDraft => ({ account_number: '', re_pp: 'real_estate', name: '', owner_name: '' });

/**
 * How the batch's accounts are being described: typed by hand, or read from uploaded
 * assessor documents. Both converge on the SAME create path below — the documents only
 * fill the draft in, they never take the create decision.
 */
type ParcelSource = 'manual' | 'documents';
const contactName = (c: PropContact) => [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || 'Contact';

export function ParcelSetupWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  // Parcel ids already created this session, keyed by draft index — so a retry
  // after a mid-batch failure SKIPS the parcels that already succeeded instead of
  // duplicating them (§3 hardening).
  // Keyed by a STABLE identity (source document id, else the account number) — not by
  // position. In document mode a reviewer can tick or untick a proposal before retrying,
  // which shifts every later index; a positional key would then hand an already-created
  // parcel's id to a different account.
  const createdRef = useRef<Record<string, string>>({});
  // Documents whose figures have already been re-staged. The attach + re-read runs after
  // a parcel is created, but a mid-batch failure sends the whole loop through again —
  // and re-reading an already-handled document opens ANOTHER staged batch of the same
  // figures, so a reviewer finds the same notice waiting two or three times.
  const readBackRef = useRef<Set<string>>(new Set());
  const [params] = useSearchParams();
  const preProp = params.get('property_id') || '';

  const [propertyId, setPropertyId] = useState(preProp);
  const [assessorId, setAssessorId] = useState('');
  const [jurisdictionIds, setJurisdictionIds] = useState<string[]>([]);
  const [parcels, setParcels] = useState<ParcelDraft[]>([emptyParcel()]);
  const [source, setSource] = useState<ParcelSource>('manual');
  const [proposals, setProposals] = useState<ParcelProposal[]>([]);
  const [linkContacts, setLinkContacts] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Who is creating these, and how they get onto the book of what they create. See
  // lib/parcelBookAssignment for why this is role-aware rather than one column.
  const me = usePermissions();
  const bookAssignment = useMemo(
    () => bookAssignmentFor(me.data?.user?.role),
    [me.data?.user?.role],
  );
  const [assignSelf, setAssignSelf] = useState<boolean | null>(null);
  // `null` means untouched, so the role's default applies once it is known — without
  // this, the default would be locked in before the permission grid resolved.
  const assignSelfEffective = assignSelf ?? bookAssignment.defaultOn;

  /**
   * The accounts this run will actually create, from whichever source is active.
   *
   * In document mode ONLY the rows a person ticked appear here. That is the whole
   * guarantee: an unconfirmed proposal has no representation downstream, so there is no
   * "create everything" path to take by accident — unlike the staged-import commit,
   * which sends an empty body and accepts every parsed row.
   */
  const confirmedProposals = useMemo(
    () => proposals.filter((p) => p.phase === 'ready' && p.confirmed),
    [proposals],
  );
  const effectiveParcels: ParcelDraft[] = useMemo(() => (
    source === 'documents'
      ? confirmedProposals.map((p) => ({
        account_number: p.accountNumber,
        re_pp: 'real_estate',
        name: '',
        owner_name: p.ownerName,
      }))
      : parcels
  ), [source, confirmedProposals, parcels]);
  /** Parallel to effectiveParcels: the document each account came from, or null. */
  const sourceDocumentIds = useMemo(() => (
    source === 'documents' ? confirmedProposals.map((p) => p.documentId) : effectiveParcels.map(() => null)
  ), [source, confirmedProposals, effectiveParcels]);

  const propertiesQuery = useQuery<PropertyRow[]>({ queryKey: ['properties-all'], queryFn: ({ signal }) => api.get<PropertyRow[]>('/api/properties', signal) });
  const property = useMemo(() => (propertiesQuery.data ?? []).find((p) => p.id === propertyId) || null, [propertiesQuery.data, propertyId]);

  const jurisdictionsQuery = useQuery<JurisdictionRow[]>({
    queryKey: ['jurisdictions-by-property', propertyId],
    queryFn: ({ signal }) => api.get<JurisdictionRow[]>(`/api/jurisdictions-by-property/${propertyId}`, signal),
    enabled: !!propertyId,
  });
  const existingParcelsQuery = useQuery<ParcelRow[]>({
    queryKey: ['parcels-by-property', propertyId],
    queryFn: ({ signal }) => api.get<ParcelRow[]>(`/api/parcels?property_id=${propertyId}`, signal),
    enabled: !!propertyId,
  });
  const assessorsQuery = useQuery<AssessorRow[]>({
    queryKey: ['assessors-by-state', property?.state],
    queryFn: ({ signal }) => api.get<AssessorRow[]>(`/api/assessors-by-state/${property!.state}`, signal),
    enabled: !!property?.state,
  });
  const contactsQuery = useQuery<PropContact[]>({
    queryKey: ['contacts-by-entity', 'properties', propertyId, 'direct'],
    queryFn: ({ signal }) => api.get<PropContact[]>(`/api/contacts-by-entity/properties/${propertyId}?direct=1`, signal),
    enabled: !!propertyId,
  });

  // Default jurisdictions to the property's set + assessor to its first parcel's,
  // whenever a (new) property loads and the user hasn't chosen yet.
  useEffect(() => {
    if (jurisdictionsQuery.data && jurisdictionIds.length === 0) setJurisdictionIds(jurisdictionsQuery.data.map((j) => j.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jurisdictionsQuery.data]);
  useEffect(() => {
    if (existingParcelsQuery.data && existingParcelsQuery.data.length && !assessorId) {
      const a = existingParcelsQuery.data[0].assessor_id;
      if (a) setAssessorId(a);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingParcelsQuery.data]);

  const jurisdictions = jurisdictionsQuery.data ?? [];
  const propContacts = contactsQuery.data ?? [];

  function onPropertyChange(id: string) {
    setPropertyId(id);
    setAssessorId('');
    setJurisdictionIds([]);
    setLinkContacts(new Set());
    // Everything below is scoped to the property that was chosen BEFORE. Proposals were
    // read against it and their documents were uploaded onto it; createdRef holds parcels
    // already created under it. Carrying either across would attach an account to a
    // property its document never mentioned, or make a retry skip a row because a
    // same-keyed parcel exists on a DIFFERENT property.
    setProposals([]);
    createdRef.current = {};
    readBackRef.current = new Set();
  }
  const setParcel = (i: number, patch: Partial<ParcelDraft>) => setParcels((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addParcel = () => setParcels((ps) => [...ps, emptyParcel()]);
  const removeParcel = (i: number) => setParcels((ps) => (ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps));
  const toggleJur = (id: string) => setJurisdictionIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleContact = (id: string) => setLinkContacts((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const canAdvance = (step: number) => {
    if (step === 0) return !!propertyId;
    if (step === 1) return !!assessorId;
    if (step === 2) {
      // In document mode the gate is a CONFIRMED account, not merely a read one — a
      // proposal nobody ticked must not let the wizard advance toward creating it.
      if (source === 'documents') return confirmedProposals.length > 0;
      return parcels.length > 0 && parcels.every((p) => p.account_number.trim() !== '');
    }
    return true;
  };

  async function submit() {
    setSubmitting(true);
    try {
      const year = new Date().getFullYear();
      const createdIds: string[] = [];
      const readBackFailures: string[] = [];
      for (let i = 0; i < effectiveParcels.length; i++) {
        const p = effectiveParcels[i];
        const documentId = sourceDocumentIds[i];
        // Reuse an id created on a prior (failed) attempt so a retry never
        // duplicates a parcel that already succeeded. Keyed by the DOCUMENT (or the
        // account number), never by position: in document mode a reviewer may tick or
        // untick a row before retrying, which shifts every index after it and would
        // otherwise hand the wrong parcel's id to the wrong account.
        // Document mode keys by the DOCUMENT, because ticking a proposal shifts every
        // later index. Manual mode keys by POSITION, because two drafts may legitimately
        // share an account number — a real-estate and a personal-property parcel on the
        // same account is the documented case — and keying those by account would make
        // the second one reuse the first's id, creating ONE parcel while the toast
        // reported two.
        const retryKey = documentId || `row:${i}`;
        let id = createdRef.current[retryKey];
        if (!id) {
          // Put the account on the creator's book where that was asked for (or is
          // money-free and therefore automatic). Without it a booked creator opens an
          // account that is immediately 404 to them — the property is visible via a
          // SIBLING parcel's assignment, and a brand-new parcel has none of its own.
          const bookColumn = bookAssignment.column && assignSelfEffective && me.data?.user?.contact_id
            ? { [bookAssignment.column]: me.data.user.contact_id }
            : {};
          const res = await api.post<{ id: string }>('/api/parcels', {
            property_id: propertyId,
            assessor_id: assessorId,
            account_number: p.account_number.trim(),
            re_pp: p.re_pp,
            name: p.name.trim() || null,
            owner_name: p.owner_name.trim() || null,
            state: property?.state || null,
            year,
            status: 'active',
            ...bookColumn,
          });
          id = res.id;
          createdRef.current[retryKey] = id;
        }
        createdIds.push(id);
        if (jurisdictionIds.length) {
          // Idempotent (UNIQUE parcel×jurisdiction×year) — safe to re-run on retry.
          await api.post('/api/tax-records/bulk', { parcel_id: id, jurisdiction_ids: jurisdictionIds, tax_year: year });
        }
        if (documentId && !readBackRef.current.has(documentId)) {
          // The document now has a record to belong to. Attaching it is what makes the
          // notice findable from the account it opened.
          //
          // Then re-read it. The first read happened while the account did not exist, so
          // the reader could not resolve it and DISCARDED the figures — on the real
          // Tarrant tax bill that meant all six jurisdiction lines were dropped. The
          // re-read reuses the stored reading, so it costs no further model call, and
          // this time the account resolves and the figures stage into the ordinary
          // review. Money is never written here; it lands where money is reviewed.
          //
          // Both are best-effort: the account is already created and correct, and
          // failing the whole batch over a read-back would strand it. What goes wrong is
          // NAMED at the end rather than swallowed.
          try {
            await api.put(`/api/documents/${documentId}`, { parcel_id: id });
            const reread = await api.post<{ status?: string; rows_staged?: number; queued?: boolean; error?: string | null }>(
              `/api/document-extract/document/${documentId}`, { reuse_reading: true },
            );
            // The route answers 200 for a read that FAILED and for one that staged
            // nothing, so the response has to be inspected. Announcing "the figures are
            // staged" without looking is how a silent loss becomes a confident lie —
            // and staging nothing is the expected outcome whenever the account still
            // does not resolve.
            const staged = reread.queued
              ? true // a worker will finish it; the operator is told below that it is pending
              : reread.status === 'succeeded' && (reread.rows_staged ?? 0) > 0;
            if (staged) readBackRef.current.add(documentId);
            else readBackFailures.push(p.account_number.trim());
          } catch {
            readBackFailures.push(p.account_number.trim());
          }
        }
      }
      // §3.3 — extend the chosen existing property contacts to the new parcels (idempotent link).
      const parcelTargets = createdIds.map((id) => ({ entity_type: 'parcels', entity_id: id }));
      for (const cid of linkContacts) {
        await api.post('/api/contact-links', { contact_id: cid, targets: parcelTargets });
      }
      // Refresh the destination so the new parcels appear immediately (§6 stale-UI fix).
      qc.invalidateQueries({ queryKey: ['parcels-list'] });
      if (propertyId) {
        qc.invalidateQueries({ queryKey: ['parcels-by-property', propertyId] });
        qc.invalidateQueries({ queryKey: ['property', propertyId] });
        qc.invalidateQueries({ queryKey: ['contacts-by-entity', 'properties', propertyId, 'direct'] });
      }
      createdRef.current = {};
      readBackRef.current = new Set();
      const base = `${createdIds.length} parcel${createdIds.length === 1 ? '' : 's'} created.`;
      if (readBackFailures.length) {
        toast({
          variant: 'warning',
          message: `${base} The figures on ${readBackFailures.length} document${readBackFailures.length === 1 ? '' : 's'} could not be re-read (${readBackFailures.join(', ')}) — read those documents again from the account.`,
        });
      } else {
        toast({
          variant: 'success',
          message: source === 'documents'
            ? `${base} Their figures are staged for review — nothing was written to the accounts yet.`
            : base,
        });
      }
      navigate(preProp ? `/properties/${preProp}` : '/parcels');
    } catch (err) {
      const created = Object.keys(createdRef.current).length;
      const base = err instanceof ApiClientError ? err.message : 'Could not finish creating the parcels.';
      toast({
        variant: 'error',
        message: created > 0
          ? `${base} — ${created} parcel${created === 1 ? ' was' : 's were'} already created; retry to finish the rest.`
          : base,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-tight)', color: 'var(--color-ink)' }}>New Parcel Setup</h1>
        <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          Add one or several parcels to a property in a single pass — shared property, assessor, and jurisdictions carry across the batch.
        </p>
      </header>

      <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-6)' }}>
        <Wizard
          steps={[
            { label: 'Property', description: 'Which property' },
            { label: 'Shared setup', description: 'Assessor + jurisdictions' },
            { label: 'Parcels', description: 'One or many' },
            { label: 'Contacts & review', description: 'Coverage + create' },
          ]}
          onSubmit={submit}
          onCancel={() => navigate(preProp ? `/properties/${preProp}` : '/parcels')}
          submitting={submitting}
          submitLabel={`Create ${effectiveParcels.length} parcel${effectiveParcels.length === 1 ? '' : 's'}`}
          canAdvance={canAdvance}
        >
          {({ currentStep }) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              {currentStep === 0 && (
                <Field label="Property" hint={preProp ? 'Pre-selected from the property you came from.' : 'The property these parcels belong to.'}>
                  {/* Searchable typeahead property picker — same interaction as the
                      "+ New Property" wizard's client box. When ?property_id= locks the
                      property (preProp), it renders disabled showing the chosen property. */}
                  <SearchableSelect
                    options={(propertiesQuery.data ?? [])
                      .map((p) => ({ value: p.id, label: p.name || p.id }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                    value={propertyId || null}
                    onChange={(v) => onPropertyChange(v ?? '')}
                    placeholder="Search properties…"
                    disabled={!!preProp}
                  />
                </Field>
              )}

              {currentStep === 1 && (
                <>
                  <Field label="Assessor" hint="Shared by every parcel in this batch.">
                    <Select
                      options={[{ value: '', label: assessorsQuery.isLoading ? 'Loading…' : '— Select assessor —' }, ...(assessorsQuery.data ?? []).map((a) => ({ value: a.id, label: a.name || a.id }))]}
                      value={assessorId}
                      onChange={(e) => setAssessorId(e.target.value)}
                    />
                    {!assessorsQuery.isLoading && !property?.state && (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
                        This property has no state on file, so assessors can't be listed. Set the property's state first, then come back.
                      </span>
                    )}
                    {!assessorsQuery.isLoading && property?.state && (assessorsQuery.data ?? []).length === 0 && (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
                        No assessors on file for {property.state}. Add an assessor for {property.state} (Government → Assessors) before creating parcels here.
                      </span>
                    )}
                  </Field>
                  <Field label="Jurisdictions" hint="Inherited from the property; applies to the whole batch. Uncheck any that don't apply.">
                    {jurisdictionsQuery.isLoading ? (
                      <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
                    ) : jurisdictions.length === 0 ? (
                      <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>This property has no jurisdictions on file.</p>
                    ) : (
                      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
                        {jurisdictions.map((j) => (
                          <label key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)' }}>
                            <input type="checkbox" checked={jurisdictionIds.includes(j.id)} onChange={() => toggleJur(j.id)} />
                            {j.name || j.id}
                          </label>
                        ))}
                      </div>
                    )}
                  </Field>
                </>
              )}

              {currentStep === 2 && (
                <>
                  <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }} role="group" aria-label="How to describe these parcels">
                    {([['manual', 'Type them in'], ['documents', 'Read from documents']] as const).map(([key, label]) => (
                      <Button
                        key={key}
                        variant={source === key ? 'primary' : 'secondary'}
                        size="sm"
                        aria-pressed={source === key}
                        onClick={() => setSource(key)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>

                  {source === 'documents' && (
                    <ParcelDocumentIntake
                      propertyId={propertyId}
                      propertyState={property?.state ?? null}
                      proposals={proposals}
                      onChange={setProposals}
                      disabled={submitting}
                    />
                  )}

                  {source === 'manual' && parcels.map((p, i) => (
                    <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Parcel {i + 1}</span>
                        {parcels.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeParcel(i)} style={{ color: 'var(--color-status-red)' }}>Remove</Button>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 'var(--sp-3)' }}>
                        <Field label="Account number"><Input value={p.account_number} onChange={(e) => setParcel(i, { account_number: e.target.value })} autoFocus={i === 0} /></Field>
                        <Field label="RE / PP">
                          <Select options={[{ value: 'real_estate', label: 'Real estate' }, { value: 'personal_property', label: 'Personal property' }]} value={p.re_pp} onChange={(e) => setParcel(i, { re_pp: e.target.value })} />
                        </Field>
                        <Field label="Name (optional)"><Input value={p.name} onChange={(e) => setParcel(i, { name: e.target.value })} /></Field>
                        <Field label="Owner (optional)"><Input value={p.owner_name} onChange={(e) => setParcel(i, { owner_name: e.target.value })} /></Field>
                      </div>
                      {/* Non-blocking duplicate-account warning, per parcel. */}
                      <ParcelDuplicateWarning values={{ account_number: p.account_number, state: property?.state ?? '' } as FormValues} />
                    </div>
                  ))}
                  {source === 'manual' && (
                    <div>
                      <Button variant="secondary" size="sm" onClick={addParcel}>+ Add another parcel</Button>
                    </div>
                  )}
                </>
              )}

              {currentStep === 3 && (
                <>
                  <div style={{ padding: 'var(--sp-3) var(--sp-4)', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
                    Creating <strong>{effectiveParcels.length}</strong> parcel{effectiveParcels.length === 1 ? '' : 's'} under <strong>{property?.name || 'the property'}</strong>
                    {jurisdictionIds.length ? <> with <strong>{jurisdictionIds.length}</strong> jurisdiction{jurisdictionIds.length === 1 ? '' : 's'} each</> : null}.
                  </div>
                  {bookAssignment.label && (
                    <Field label="Your access" hint={bookAssignment.note ?? undefined}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
                        <input
                          type="checkbox"
                          checked={assignSelfEffective}
                          onChange={() => setAssignSelf(!assignSelfEffective)}
                        />
                        {bookAssignment.label}
                      </label>
                    </Field>
                  )}
                  {propContacts.length > 0 && (
                    <Field label="Extend an existing contact?" hint="This property already has contact(s). Add the new parcel(s) to a contact's coverage, or leave unchecked to add parcel-level contacts later.">
                      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
                        {propContacts.map((c) => (
                          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)' }}>
                            <input type="checkbox" checked={linkContacts.has(c.id)} onChange={() => toggleContact(c.id)} />
                            {contactName(c)}{c.role ? <span style={{ color: 'var(--color-ink-3)' }}> · {c.role.replace(/_/g, ' ')}</span> : null}
                          </label>
                        ))}
                      </div>
                    </Field>
                  )}
                </>
              )}
            </div>
          )}
        </Wizard>
      </div>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{hint}</span>}
    </label>
  );
}
